// Fully serializable match state. Contains NO Phaser / DOM references so it can be
// run authoritatively on the server and snapshotted to the client over Socket.IO.

import type { AvatarConfig } from '../avatar/types';
import type { RankedStanding } from '../rating/leagues';

/** Player seat index in a match. */
export type Seat = 0 | 1;

export function otherSeat(seat: Seat): Seat {
  return seat === 0 ? 1 : 0;
}

/**
 * How eager the engine is to pause for optional trap prompts for a seat.
 * `always` asks at every legal timing; `auto` skips empty phase windows;
 * `minimal` asks only when a set trap answers the play that just happened,
 * plus one prompt for a Set Just Desserts at the start of the opponent's
 * Battle Phase when they control a monster, and a Set Ultimate Offering at
 * that start and again when an attack is declared. Counter Traps (Solemn Judgment,
 * Magic Jammer, Seven Tools of the Bandit, Horn of Heaven) count as an
 * answer only when the opponent activated or summoned something they can
 * negate. `off` never prompts (chains included). A face-up Ultimate Offering
 * ignition still pauses in `minimal` and `off`. A Set one pauses in `minimal` only.
 */
export type ResponseMode = 'auto' | 'always' | 'minimal' | 'off';

export function parseResponseMode(value: unknown): ResponseMode {
  return value === 'always' || value === 'minimal' || value === 'off' ? value : 'auto';
}

/**
 * True on the very first turn of the match (no Battle Phase, therefore no
 * Main Phase 2). Independent of which seat won the die.
 */
export function isOpeningTurn(state: { absoluteTurnCount: number }): boolean {
  return state.absoluteTurnCount === 1;
}

export enum EnginePhase {
  DRAW = 'DP',
  STANDBY = 'SP',
  MAIN_1 = 'M1',
  BATTLE = 'BP',
  MAIN_2 = 'M2',
  END = 'EP',
}

/**
 * Internal Battle Phase steps (New Expert Rules). Not shown on the phase
 * strip — the engine uses them only to open the right response window.
 */
export type BattleStep = 'START' | 'BATTLE' | 'DAMAGE' | 'END'

/** Spell Speed as of the 2002 New Expert Rules. 3 is a Counter Trap. */
export type SpellSpeed = 1 | 2 | 3

/**
 * One link on the chain. Cards flip face-up when they join; effects resolve
 * last-in first-out after both players pass.
 */
export interface ChainLink {
  /** 1-based index in activation order. */
  link: number;
  controller: Seat;
  sourceUid: string;
  cardId: string;
  spellSpeed: SpellSpeed;
  /** Targets chosen at activation; re-checked when the link resolves. */
  targetUids: string[];
  /**
   * Cards this link would destroy, known when it joined the chain.
   * Fake Trap reads the previous link. Empty when nothing is destroyed
   * or the victims are not a known group.
   */
  destroyUids?: string[];
  /** ATK/DEF chosen for a Special Summon this link will perform. */
  position?: CardPosition;
  /**
   * One face-up position per `targetUids` entry, same order. Set when the
   * link Special Summons more than one monster (The Flute of Summoning Dragon).
   */
  positions?: CardPosition[];
  /** Summoned monster this link answered, if any (Trap Hole). */
  triggerUid: string | null;
  /** Set when a Counter Trap negates this link before it resolves. */
  negated?: boolean;
  /** ATK or DEF chosen for a damage-step guardian boost. */
  boost?: 'atk' | 'def';
  /**
   * Mandatory Graveyard trigger placed with the others that happened at the
   * same time. Spell Speed 1, but it is not an optional chain onto a Spell.
   */
  triggerKind?:
    | 'immortal'
    | 'sangan'
    | 'witch'
    | 'sword'
    | 'discardRandom'
    | 'draw1'
    | 'drawOpp2';
}

/**
 * Next phase if the turn player advances one step. Main Phase 2 only exists
 * after Battle Phase; skipping Battle (or the first-turn lock) goes to End.
 */
export function nextEnginePhase(
  phase: EnginePhase,
  openingTurn: boolean,
): EnginePhase {
  switch (phase) {
    case EnginePhase.DRAW:
      return EnginePhase.STANDBY;
    case EnginePhase.STANDBY:
      return EnginePhase.MAIN_1;
    case EnginePhase.MAIN_1:
      return openingTurn ? EnginePhase.END : EnginePhase.BATTLE;
    case EnginePhase.BATTLE:
      return EnginePhase.MAIN_2;
    case EnginePhase.MAIN_2:
      return EnginePhase.END;
    case EnginePhase.END:
      return EnginePhase.DRAW;
    default:
      return EnginePhase.DRAW;
  }
}

export enum CardPosition {
  ATTACK = 'ATK',
  DEFENSE = 'DEF',
}

export interface StatModifier {
  atk: number;
  def: number;
  /** uid of the card (equip/field) that applied the modifier, for clean removal. */
  source: string;
}

/** A card id the planner is allowed to remember after a peek or a public reveal. */
export interface KnownCardRef {
  uid: string;
  cardId: string;
}

/** A unique card instance inside a match. The `uid` is stable for the whole match. */
export interface CardInstance {
  uid: string;
  cardId: string;
  /**
   * Seat that owns this copy (the deck/extra it was dealt from). Destruction,
   * bounce and banish send it home even if another player currently controls it
   * (Monster Reborn, Change of Heart).
   */
  owner: Seat;
  /** House print rarity for this copy. Hidden cards are forced to Rare. */
  foil?: string;
  faceUp: boolean;
  position: CardPosition;
  summonedThisTurn: boolean;
  attackedThisTurn: boolean;
  /**
   * Set when this monster declares an attack. A Replay clears
   * `attackedThisTurn` so it may declare again later in the Battle Phase,
   * but this flag stays: Main Phase 2 still cannot change its position.
   */
  declaredAttackThisTurn: boolean;
  positionChangedThisTurn: boolean;
  /** Absolute turn number on which the card was set face-down (trap activation delay). */
  setOnTurn: number;
  statModifiers: StatModifier[];
  /** uids of equip spells attached to this monster. */
  equippedUids: string[];
  /** if this card is an equip spell currently attached, the host monster uid. */
  equippedToUid: string | null;
  /** if this monster is under temporary control, the owner seat it returns to at End Phase. */
  controlReturnTo?: Seat;
  /**
   * Remaining turns this face-up card stays on the field (Swords of Revealing
   * Light, and later lingering cards). Cleared when the card leaves the field.
   */
  turnCounter?: number;
  /**
   * Sanga, Kazejin, and Suijin already used their effect while this copy
   * has been face-up. Cleared when the card leaves the field.
   */
  faceUpEffectUsed?: boolean;
  /**
   * ATK used for the rest of this damage calculation, ignoring printed ATK
   * and modifiers. Sanga, Kazejin, and Suijin set the attacker to 0.
   */
  atkFinal?: number;
  /**
   * Time Wizard or Barrel Dragon already used its ignition this turn.
   * Cleared when that player's turn starts again, and when the card leaves
   * the field.
   */
  ignitionUsedThisTurn?: boolean;
  /**
   * Gate Guardian or Harpie Lady Sisters was Special Summoned by its own
   * procedure. Remembered in the Graveyard and while banished. Forgotten
   * when the card returns to the hand or the Deck.
   */
  properlySummoned?: boolean;
  /**
   * Absolute turn on which Electric Lizard forbids this monster from
   * attacking. Cleared when the monster leaves the field.
   */
  lizardLockTurn?: number;
  /**
   * Shield & Sword switched this monster's printed ATK and DEF. Modifiers
   * stay on the same stat. Cleared when the turn ends or the monster leaves
   * the field. A later summon is not marked.
   */
  baseSwapped?: boolean;
  /**
   * Steel Scorpion: End Phases of the scorpion controller's opponent still
   * left before this monster is destroyed. The badge shows this number.
   */
  doomEndsLeft?: number;
  /** Seat that controlled Steel Scorpion when the attack connected. */
  doomSeat?: Seat;
}

export const MONSTER_ZONE_COUNT = 5;
export const SPELL_TRAP_ZONE_COUNT = 5;

export interface PlayerZones {
  deck: CardInstance[];
  extraDeck: CardInstance[];
  hand: CardInstance[];
  monsters: (CardInstance | null)[];
  spellTraps: (CardInstance | null)[];
  fieldSpell: CardInstance | null;
  graveyard: CardInstance[];
  banished: CardInstance[];
  lifePoints: number;
  /** whether this player still has their once-per-turn normal summon/set available. */
  hasNormalSummoned: boolean;
  /**
   * Extra Normal Summon/Set uses granted this turn (Ultimate Offering).
   * Spent after the regular once-per-turn has already been used.
   */
  extraNormalSummons: number;
  /**
   * Last Will activations this turn that have not Special Summoned yet.
   * Each copy adds one. Cleared when any turn ends.
   */
  lastWillCharges: number;
  /**
   * A monster this seat controlled left its Monster Zone for this seat's
   * Graveyard during the current turn, before or after Last Will resolved.
   */
  lastWillReady: boolean;
  /**
   * Offer the Special Summon at the next idle moment. A cancel clears it
   * until the next phase or the next qualifying send.
   */
  lastWillAsk: boolean;
  /**
   * Opponent monster uids that may be Tributed as if you controlled them
   * (Soul Exchange). Each activation adds one. A Level 7+ summon can use two.
   */
  soulExchangeUids: string[];
  /** Soul Exchange: skip Battle Phase this turn. */
  skipBattlePhase: boolean;
  /** turns this seat is forbidden from declaring attacks (e.g. Swords of Revealing Light). */
  attackLockTurns: number;
}

export interface PlayerInfo {
  id: string;
  username: string;
  /** true when this seat is controlled by the built-in AI (single player). */
  isAI: boolean;
  connected: boolean;
  /** Paper-doll look. Always a parsed config on the wire. */
  avatar: AvatarConfig;
  /**
   * Catalog playmat id for this seat. Present in online PvP so each client
   * can draw the other duelist's half. Local vs-AI omits it (the renderer
   * rolls a CPU mat).
   */
  matId?: string;
  /**
   * Catalog sleeve id for this seat. Present in online PvP so each client
   * and each spectator draws that duelist's card back. Local vs-AI omits
   * the CPU (the renderer rolls one).
   */
  sleeveId?: string;
  /**
   * Public league at match start. Present on human PvP seats so both clients
   * (and spectators) can show the medal. Omitted vs AI and when the lookup fails.
   */
  standing?: RankedStanding;
  /**
   * CPU planner only. How much of the opponent's hidden field this seat may
   * use: Easy = public cards, Normal = Set spells/traps, Hard+ = those plus
   * face-down monsters. Omitted on human seats (and treated as Normal).
 */
  aiDifficulty?: 'test' | 'easy' | 'normal' | 'hard' | 'expert' | 'master';
}

export type MatchStatus = 'waiting' | 'active' | 'finished';

/**
 * An open response window: the turn is paused so a player may decide whether to
 * activate a set trap (e.g. Trap Hole in response to a summon). While set, only the
 * responder may act, and only by sending RESPOND_PRIORITY.
 */
/**
 * An open hand-size-limit discard: the turn cannot pass until this seat discards
 * down to the hand limit. While set, only that seat may act, and only by discarding.
 */
export interface PendingDiscard {
  /** the seat that must discard. */
  seat: Seat;
  /** how many cards still need to be discarded. */
  count: number;
}

/**
 * Stable id for a trap-response window. The English `prompt` stays on the
 * wire; the client translates `promptCode`.
 */
export type DuelPromptCode =
  | 'trapOnSummon'
  | 'trapOnAttack'
  | 'trapOnDamage'
  | 'trapActivate'
  | 'chainTo';

export interface PendingPriority {
  /** correlation id echoed back by RESPOND_PRIORITY. */
  requestId: string;
  /** the seat allowed to respond. */
  responder: Seat;
  /** human-readable description of what triggered the window. */
  prompt: string;
  /** i18n code for `prompt`. English text remains the fallback. */
  promptCode: DuelPromptCode;
  /** uids of the responder's set traps that may be activated right now. */
  activatableUids: string[];
  /** the card that triggered the window (e.g. the summoned monster) to auto-target. */
  triggerUid: string | null;
  /** Chain link being answered, when this window is a chain-response. */
  chainLink?: number;
}

/**
 * A battle waiting on a trap-response window. An attack declared against a monster
 * pauses before damage calculation so the defender may answer it; once the response
 * is settled the engine resolves this battle, or cancels it when the trap left the
 * attacker unable to continue (destroyed, or switched out of Attack Position).
 */
export interface PendingAttack {
  /** the attacking player. */
  seat: Seat;
  attackerUid: string;
  /** the attacked monster, or null for a direct attack on life points. */
  targetUid: string | null;
  /**
   * declared: Battle Step window just opened or closed.
   * preDamage: Damage Step flip is done; waiting on / past the pre-damage window.
   */
  stage: 'declared' | 'preDamage';
  /** Defender was face-down and flipped for this battle (Flip Effect after calc). */
  revealedByAttack?: boolean;
  /**
   * Monsters the defending player controlled at declaration. A change (or
   * the attack target leaving) during the Battle Step is a Replay.
   */
  defenderCount: number;
}

/**
 * An open Flip-Effect target selection: a monster's Flip Effect resolved and its
 * controller must choose a target (e.g. Reaper of the Cards picking one of the
 * opponent's Set cards blindly). While set, only the chooser may act, and only by
 * answering with RESOLVE_FLIP_TARGET. The turn is paused meanwhile.
 */
export interface PendingFlipEffect {
  /** correlation id echoed back by RESOLVE_FLIP_TARGET. */
  requestId: string;
  /** the seat that owns the flipped monster and must pick the target. */
  chooser: Seat;
  /** the flipped monster (stays on the field until this window closes). */
  sourceUid: string;
  cardId: string;
  /** human-readable description of the choice. */
  prompt: string;
  /** candidate target uids the chooser may pick from. */
  targetUids: string[];
}

/**
 * An open activation target selection: the spell/trap is already on the field
 * (SPELL_ACTIVATED / TRAP_ACTIVATED has fired) and the controller must pick
 * before the effect resolves. While set, only the chooser may act, and only by
 * answering with SELECT_TARGETS.
 */
export type CoinSide = 'heads' | 'tails';

/**
 * This seat must order simultaneous mandatory effects before they are placed.
 * The first card they choose is Chain Link 1 and resolves last.
 */
export interface PendingChainOrder {
  requestId: string;
  chooser: Seat;
  /** Every card must be ordered. First chosen is Chain Link 1 and resolves last. */
  cards: { uid: string; cardId: string }[];
}

/**
 * The turn is paused until this seat calls Heads or Tails.
 * Time Wizard (Main Phase) and Jirai Gumo (on attack declaration).
 */
export interface PendingCoinCall {
  requestId: string;
  chooser: Seat;
  cardUid: string;
  cardId: string;
  kind: 'timeWizard' | 'jirai';
}

/**
 * Optional yes/no that is not a coin.
 * Blast Juggler in your Standby Phase, or Mushroom Man #2 at your End Phase.
 */
export interface PendingChoice {
  requestId: string;
  chooser: Seat;
  cardUid: string;
  cardId: string;
  kind: 'blastJuggler' | 'mushroomGive';
}

export interface PendingTargets {
  /** correlation id echoed back by SELECT_TARGETS. */
  requestId: string;
  /** the seat that activated the card and must pick. */
  chooser: Seat;
  /** the resolving spell/trap sitting on the field. */
  sourceUid: string;
  cardId: string;
  prompt: string;
  /** candidate target uids the chooser may pick from. */
  candidateUids: string[];
  min: number;
  max: number;
  /** ATK/DEF already chosen for a Special Summon this activation will perform. */
  position?: CardPosition;
  /**
   * Last Will: SELECT_TARGETS Special Summons from the Deck instead of
   * resolving the Spell again.
   */
  specialSummonFromDeck?: boolean;
  /**
   * The chosen monster is destroyed. It is not a Spell activation.
   * Dream Clown and the same window for a face-up destroy.
   */
  destroyChosenMonster?: boolean;
  /**
   * Barrel Dragon. The monster is chosen first. The coins are tossed
   * when this window closes. 2 or 3 heads destroys that monster.
   */
  barrelTarget?: boolean;
  /**
   * Thunder Dragon, Sangan, or Witch of the Black Forest. The chosen
   * cards move from the chooser's Deck to their hand, then the Deck
   * is shuffled.
   */
  addToHandFromDeck?: boolean;
  /**
   * Elegant Egotist. The chosen monster is Special Summoned from the hand
   * or Deck when the Spell resolves. An empty pick declines the summon.
   */
  specialSummonNamed?: boolean;
  /**
   * Fissure resolving a tie for the lowest ATK. The chosen face-up monster
   * is destroyed, then the chain continues.
   */
  fissureDestroy?: boolean;
  /** Crass Clown. The chosen monster returns to its owner's hand. */
  returnChosenToHand?: boolean;
  /**
   * Yado Karu. The chosen hand cards go to the bottom of the Deck in
   * this order. An empty pick declines the effect.
   */
  toDeckBottom?: boolean;
  /**
   * Cannon Soldier, Catapult Turtle, or The Little Swordsman of Aile.
   * The chosen monster is Tributed, then the ignition resolves.
   */
  tributeIgnition?: 'cannon' | 'turtle' | 'aile';
  /** Blast Juggler. Tribute it, then destroy these 2 monsters. */
  blastDestroy?: boolean;
  /**
   * Tribute to the Doomed. This card is the discard cost. The monster
   * to destroy is chosen in the next window.
   */
  discardCost?: boolean;
  /** Share the Pain. The opponent Tributes this monster, for no effect. */
  opponentTribute?: boolean;
  /**
   * Big Eye. These Deck cards are put back on top in the chosen order.
   * The first uid is the new top (the next draw).
   */
  reorderDeckTop?: boolean;
}

export interface MatchState {
  id: string;
  seats: [PlayerInfo, PlayerInfo];
  zones: [PlayerZones, PlayerZones];
  turnPlayer: Seat;
  phase: EnginePhase;
  turnCount: number;
  /** increments at the start of every player's turn (used for trap set timing). */
  absoluteTurnCount: number;
  status: MatchStatus;
  winner: Seat | null;
  winReason: string | null;
  /** deterministic RNG cursor so shuffles are reproducible/authoritative. */
  rngState: number;
  /** open trap-response window, or null when no player owes a response. */
  pendingPriority: PendingPriority | null;
  /** battle deferred until an open trap-response window is settled. */
  pendingAttack: PendingAttack | null;
  /**
   * Open chain (activation order). Empty when nothing is waiting to resolve.
   * Must live on the snapshot — the AI planner rebuilds GameEngine from state.
   */
  chain: ChainLink[];
  /** Consecutive passes on the current chain window. Resolves at 2. */
  chainPasses: number;
  /**
   * Summoned monster the current window/chain is answering (Trap Hole).
   * Null for attacks, phase windows, and a chain that started from a spell.
   */
  chainTriggerUid: string | null;
  /** Internal Battle Phase step, or null outside BP. */
  battleStep: BattleStep | null;
  /** Phase to enter after the Battle Phase End Step (M2 or EP). */
  battleLeaveTo: EnginePhase | null;
  /** open end-of-turn discard requirement, or null when nobody owes a discard. */
  pendingDiscard: PendingDiscard | null;
  /** open Flip-Effect target selection, or null when none is pending. */
  pendingFlipEffect: PendingFlipEffect | null;
  /** open activation target selection, or null when the effect may resolve. */
  pendingTargets: PendingTargets | null;
  /**
   * Ultimate Offering: this seat paid (or just flipped it in a legal ignition
   * window on the opponent's Battle Phase) and may Normal Summon/Set now.
   * ADVANCE_PHASE from that seat means "done" and does not skip the phase.
   */
  pendingOfferingSummon: Seat | null;
  /** Open Heads/Tails call, or null. */
  pendingCoinCall: PendingCoinCall | null;
  /** Blast Juggler or Mushroom Man #2 waiting for yes or no. */
  pendingChoice: PendingChoice | null;
  /**
   * This seat must order their simultaneous mandatory effects before those
   * links are placed. The first uid they send is Chain Link 1 (resolves last).
   */
  pendingChainOrder: PendingChainOrder | null;
  /** Reverse Trap: invert ATK/DEF modifiers until this turn's End Phase. */
  reverseStatMods: boolean;
  /** Waboku: that seat takes 0 battle damage for the rest of this turn. */
  battleDamageImmune: [boolean, boolean];
  /** Waboku: that seat's monsters cannot be destroyed by battle this turn. */
  battleDestroyImmune: [boolean, boolean];
  /**
   * Per-seat trap-prompt appetite. Private: the opponent's value is masked
   * in getStateForSeat (it is bluff material).
   */
  responseMode: [ResponseMode, ResponseMode];
  /**
   * Spell/trap uids that already passed a summon window where Trap Hole was
   * legal (face-up ATK >= 1000). Planner memory. Stripped in getStateForSeat.
   */
  trapHolePassedUids: string[];
  /**
   * Spell/trap uids that already declined a battle window. Planner memory.
   * Stripped in getStateForSeat.
   */
  battlePassedUids: string[];
  /**
   * Cards each seat has actually seen (peek or public reveal). Face-down ids
   * outside this list stay hidden to the planner. Stripped for the other seat.
   */
  aiKnownCards: [KnownCardRef[], KnownCardRef[]];
  /**
   * Perspective-only: the viewer's spell/trap uids (hand or field) that can be
   * activated right now. Populated per-seat by getStateForSeat; absent otherwise.
   */
  activatableUids?: string[];
}

export const STARTING_LIFE_POINTS = 8000;
export const STARTING_HAND_SIZE = 5;
/** Maximum number of cards a player may hold at the end of their turn. */
export const HAND_SIZE_LIMIT = 6;
/**
 * How long Standby stays visible before the host auto-advances to Main Phase 1.
 * Long enough to read the phase highlight; skippable by clicking M1.
 */
export const STANDBY_HOLD_MS = 1000;

/** A zone locator used by actions/events to address a specific slot on the field. */
export interface ZoneRef {
  seat: Seat;
  zone: 'deck' | 'extraDeck' | 'hand' | 'monsters' | 'spellTraps' | 'fieldSpell' | 'graveyard' | 'banished';
  /** slot index for array-backed zones (monsters / spellTraps). */
  index?: number;
}
